import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, AuthRoutes } from '@core';
import { take } from 'rxjs';
import { ResetPasswordHandler } from '../../application/handlers/reset-password.handler';

interface ResetFormShape {
  email: FormControl<string>;
}

@Component({
  selector: 'auth-reset-password-page',
  templateUrl: './reset-password.page.html',
  styleUrls: ['./reset-password.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ResetPasswordPage {
  readonly loginPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.LOGIN}`;

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  readonly form: FormGroup<ResetFormShape> = new FormGroup<ResetFormShape>({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    })
  });

  private readonly resetPasswordHandler: ResetPasswordHandler = inject(ResetPasswordHandler);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const email: string = this.form.controls.email.value;
    this.loading.set(true);
    this.errorMessage.set(null);
    this.resetPasswordHandler
      .handle({ email })
      .pipe(take(1))
      .subscribe({
        next: (): void => {
          this.loading.set(false);
          void this.router.navigate([`/${AppRoutes.AUTH}/${AuthRoutes.RESET_PASSWORD_SENT}`], {
            queryParams: { email }
          });
        },
        error: (err: Error): void => {
          this.loading.set(false);
          this.errorMessage.set(err.message);
        }
      });
  }

  goBack(): void {
    void this.router.navigate([this.loginPath]);
  }

  emailError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.email;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.emailFormat');
    return null;
  }
}
